import { memo, useEffect, useMemo, useRef, useState } from "react"
import { format, parseISO } from "date-fns"
import {
	IconCalendarXmarkOutline18 as CalendarXmarkIcon,
	IconPlusOutline18 as PlusIcon,
	IconXmarkOutline18 as RemoveIcon,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import type { Project } from "@productlane/zero"
import { projectSelectStatusOrder } from "@productlane/api/src/lib/constants"
import { cn, USER_COLOR_PALETTE } from "@productlane/lib/src/browser"
import {
	Alert,
	Avatar,
	Button,
	Dialog,
	Form,
	FormTextarea,
	IconButton,
	Input,
	LinearLabelsIcon,
	LinearLeadIcon as LeadIcon,
	LinearMembersIcon as MembersIcon,
	LinearStartDateIcon as CalendarIcon,
	LinearTargetDateIcon as TargetIcon,
	Popover,
	ProjectIconKeys,
	ProjectStatusIcons,
	ScrollArea,
	ScrollViewport,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"
import { Calendar } from "@productlane/ui/src/components/internal/calendar"
import { InternalDialogFooter } from "@productlane/ui/src/components/internal/dialog"

import { ColorPicker } from "@/components/color-picker"
import { LabelSelect } from "@/components/label-select"
import { PopoverButton } from "@/components/popover-button"
import { toast } from "@/components/toast"
import { toggleLabelId } from "@/components/toggle-label-id"
import { LinearPriorityIcon } from "@/constants"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { getProjectIcon } from "@/lib/projects"
import { useZeroWorkspace } from "@/lib/zero"
import { CHIP_CLASS } from "../issues/create-issue-more-options"
import { resolveDefaultTeamId } from "../threads/hooks/resolve-default-team-id"
import { useSelectedTeams } from "../threads/hooks/useSelectedTeams"

export type PartialProjectCreate = Pick<
	Project,
	| "id"
	| "name"
	| "color"
	| "icon"
	| "description"
	| "progress"
	| "state"
	| "url"
	| "sortOrder"
	| "targetDate"
	| "linearProjectId"
>

interface CreateProjectDialogProps {
	isOpen: boolean
	onOpenChange: (
		value: boolean,
		created?: PartialProjectCreate & {
			internalUpdate?: {
				isVisible?: boolean
				notUpdateVisibility?: boolean
			}
		},
	) => void
	title?: string
	defaultIcon?: ProjectIconKeys
	defaultColor?: string
	// if there is a tag id it means we want to convert it to a project
	tagId?: string
	highlighted?: string
	defaultStatus?: string
}

const CreateProjectFormSchema = z.object({
	name: z
		.string()
		.min(1, { message: "Project name must contain at least 1 character(s)." })
		.max(80, { message: "Name cannot be longer than 80 characters." }),
	description: z
		.string()
		.max(255, { message: "Summary cannot be longer than 255 characters." })
		.optional(),
	color: z.string().min(1),
	icon: z.string().min(1),
	teamId: z.string().min(1),
	status: z.string().min(1),
	priority: z.string().optional(),
	leadId: z.string().optional(),
	startDate: z.string().optional(),
	targetDate: z.string().optional(),
	memberIds: z.array(z.string()).optional(),
	labelIds: z.array(z.string()).optional(),
	milestones: z
		.array(
			z.object({
				id: z.string(),
				name: z.string().max(80),
				targetDate: z.string().optional(),
			}),
		)
		.optional(),
})

/** Date chip that opens a calendar and keeps the picked day as yyyy-MM-dd. */
const ProjectDateChip = ({
	value,
	label,
	Icon,
	className,
	onChange,
}: {
	value?: string
	label: string
	Icon: typeof CalendarIcon
	className?: string
	onChange: (value: string | undefined) => void
}) => {
	const [open, setOpen] = useState(false)
	const date = value ? parseISO(value) : undefined
	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
			className="w-auto p-0"
			content={
				<div className="flex flex-col">
					<Calendar
						mode="single"
						selected={date}
						defaultMonth={date}
						onSelect={(next) => {
							if (next) onChange(format(next, "yyyy-MM-dd"))
							setOpen(false)
						}}
						autoFocus
					/>
					{date && (
						<div className="border-t border-gray-200 p-2 dark:border-gray-700/80">
							<Button
								variant="ghost"
								className="w-full justify-start"
								Icon={CalendarXmarkIcon}
								onClick={() => {
									onChange(undefined)
									setOpen(false)
								}}
							>
								Remove date
							</Button>
						</div>
					)}
				</div>
			}
		>
			<Button
				variant="outline"
				className={cn(
					CHIP_CLASS,
					!date && "text-tertiary dark:text-secondary",
					className,
				)}
				Icon={Icon}
			>
				{date ? format(date, "MMM d") : label}
			</Button>
		</Popover>
	)
}

export const CreateProjectDialog = memo(
	({
		isOpen,
		onOpenChange,
		title,
		defaultColor,
		defaultIcon,
		tagId,
		highlighted,
		defaultStatus,
	}: CreateProjectDialogProps) => {
		const workspace = useZeroWorkspace()

		const convertToProjectMutation =
			api.linear.convertTagToProject.useMutation()

		const createProjectMutation = api.linear.createProject.useMutation()

		const selectedTeams = useSelectedTeams()
		const defaultTeamId = resolveDefaultTeamId(
			workspace?.defaultLinearTeamId,
			selectedTeams,
		)

		const teamOptions = useMemo(
			() =>
				(selectedTeams || []).map(({ icon, color, id, name }) => {
					const Icon = getProjectIcon(icon)
					return {
						label: (
							<div className="flex items-center">
								<Icon style={{ color }} />
								<span className="ml-2">{name}</span>
							</div>
						),
						value: id,
					}
				}),
			[selectedTeams],
		)

		const { data: organization } = api.linear.organization.useQuery(undefined, {
			cacheTime: Infinity,
			staleTime: Infinity,
			enabled: isOpen,
		})

		const { data: priorities } = api.linear.priorities.useQuery(undefined, {
			cacheTime: Infinity,
			staleTime: Infinity,
			enabled: isOpen,
		})

		const priorityOptions = useMemo(
			() =>
				(priorities ?? []).map(({ label, priority }) => {
					const Icon = LinearPriorityIcon[priority]
					return {
						label: (
							<div className="flex items-center">
								{!!Icon && <Icon className="h-4 w-4 flex-none" />}
								<span className="ml-2 truncate">{label}</span>
							</div>
						),
						value: priority.toString(),
					}
				}),
			[priorities],
		)

		const projectStatusOptions = (organization?.projectStatuses ?? [])
			.map(({ id, name, type, color, position }) => {
				const Icon = ProjectStatusIcons[type]
				return {
					label: (
						<div className="flex items-center">
							{!!Icon && <Icon className="h-3.5 w-3.5" color={color} />}
							<span className="ml-2">{name}</span>
						</div>
					),
					value: id,
					type,
					name,
					color,
					position,
				}
			})
			.sort((a, b) => {
				const typeOrder =
					(projectSelectStatusOrder[
						a.type as keyof typeof projectSelectStatusOrder
					] ?? 0) -
					(projectSelectStatusOrder[
						b.type as keyof typeof projectSelectStatusOrder
					] ?? 0)
				if (typeOrder !== 0) return typeOrder
				return a.position - b.position
			})

		const form = useZodForm({
			schema: CreateProjectFormSchema,
			mode: "onChange",
			delayError: 1,
			defaultValues: {
				name: title,
				color: defaultColor ?? USER_COLOR_PALETTE.Teal.hex,
				icon: defaultIcon ?? ProjectIconKeys.Africa,
				teamId: defaultTeamId,
				status: defaultStatus ?? organization?.projectStatuses?.[0]?.id,
				description: highlighted,
			},
		})

		useEffect(() => {
			form.reset({
				...form.getValues(),
				description: highlighted ?? "",
				status: defaultStatus ?? organization?.projectStatuses?.[0]?.id,
			})
		}, [highlighted, defaultStatus, organization?.projectStatuses, form])

		const color = form.watch("color")
		const icon = form.watch("icon")
		const teamId = form.watch("teamId")
		const status = form.watch("status")
		const priority = form.watch("priority")
		const leadId = form.watch("leadId")
		const startDate = form.watch("startDate")
		const targetDate = form.watch("targetDate")
		const memberIds = form.watch("memberIds") ?? []
		const labelIds = form.watch("labelIds") ?? []
		const milestoneRows = form.watch("milestones") ?? []

		const { data: projectLabels } = api.linear.projectLabels.useQuery(
			undefined,
			{ cacheTime: Infinity, staleTime: Infinity, enabled: isOpen },
		)
		const setMilestones = (next: typeof milestoneRows) =>
			form.setValue("milestones", next)

		const { data: members } = api.linear.members.useQuery(
			{ teamId },
			{
				cacheTime: Infinity,
				staleTime: Infinity,
				enabled: isOpen && !!teamId,
			},
		)

		const leadOptions = useMemo(
			() =>
				(members ?? [])
					.filter(({ app }) => !app)
					.map(({ id, name, avatarUrl }) => ({
						label: (
							<div className="flex min-w-0 items-center gap-2">
								<Avatar name={name} image={avatarUrl} size="xxs" />
								<span className="truncate">{name}</span>
							</div>
						),
						value: id,
					})),
			[members],
		)
		const isTeamVisible = selectedTeams.some(({ id }) => id === teamId)

		useEffect(() => {
			if (!isTeamVisible && defaultTeamId) {
				form.setValue("teamId", defaultTeamId)
			}
		}, [isTeamVisible, defaultTeamId, form])

		useEffect(() => {
			if (organization?.projectStatuses?.[0]) {
				const statusValue =
					defaultStatus ?? organization?.projectStatuses?.[0]?.id ?? ""
				form.setValue("status", statusValue)
			}
		}, [organization?.projectStatuses, form, defaultStatus])

		const ref = useRef<HTMLDivElement>(null)
		ref.current?.children?.[0]?.setAttribute("style", "height: 100%")

		const onProjectCreate = async () => {
			const values = form.getValues()
			const milestones = (values.milestones ?? [])
				.filter((milestone) => milestone.name.trim())
				.map(({ name, targetDate }) => ({ name: name.trim(), targetDate }))
			const warnFailedMilestones = (failed: string[]) => {
				if (failed.length === 0) return
				toast.error({
					title: `Couldn't add milestones in Linear: ${failed.join(", ")}`,
				})
			}
			const selectedStatus = projectStatusOptions.find(
				(s) => s.value === values.status,
			)
			if (tagId) {
				const data = await convertToProjectMutation.mutateAsync({
					tagId,
					color: values.color,
					name: values.name,
					icon: values.icon,
					linearTeamId: values.teamId,
					description: values.description ?? "",
					statusId: values.status,
					statusType: selectedStatus?.type ?? "",
					statusName: selectedStatus?.name ?? "",
					statusColor: selectedStatus?.color ?? "",
					priority: values.priority ? Number(values.priority) : undefined,
					leadId: values.leadId || undefined,
					startDate: values.startDate,
					targetDate: values.targetDate,
					milestones,
					memberIds: values.memberIds,
					labelIds: values.labelIds,
				})
				onOpenChange(false, {
					id: tagId,
					name: data.name,
					color: data.color,
					linearProjectId: data.id,
					icon: data.icon ?? null,
					progress: data.progress,
					description: data.description ?? "",
					state: data.state,
					url: data.url,
					sortOrder: data.sortOrder,
					targetDate: null,
				})
				toast.success({
					title: "Converted to project",
					descriptionAction: {
						label: `${data.name}`,
						callback: () => window.open(data.url, "_blank"),
					},
				})
				warnFailedMilestones(data.failedMilestones)
				analytics.track("convert-opportunity-to-project")
			} else {
				const id = generateId()
				const linearProjectId = generateId()
				const data = await createProjectMutation.mutateAsync({
					id,
					linearProjectId,
					color: values.color,
					name: values.name,
					icon: values.icon,
					linearTeamId: values.teamId,
					description: values.description ?? "",
					statusId: values.status,
					statusType: selectedStatus?.type ?? "",
					statusName: selectedStatus?.name ?? "",
					statusColor: selectedStatus?.color ?? "",
					priority: values.priority ? Number(values.priority) : undefined,
					leadId: values.leadId || undefined,
					startDate: values.startDate,
					targetDate: values.targetDate,
					milestones,
					memberIds: values.memberIds,
					labelIds: values.labelIds,
					isVisible: null,
				})
				onOpenChange(false, {
					id,
					name: data.name,
					color: data.color,
					icon: data.icon ?? null,
					progress: data.progress,
					description: data.description ?? "",
					state: data.state,
					url: data.url,
					sortOrder: data.sortOrder,
					targetDate: null,
					linearProjectId: data.id,
				})
				toast.success({
					title: "Project created",
					descriptionAction: {
						label: `${data.name}`,
						callback: () => window.open(data.url, "_blank"),
					},
				})
				warnFailedMilestones(data.failedMilestones)
				analytics.track("create-project")
			}
		}

		useShortcut(
			"meta+enter, ctrl+enter",
			(e) => {
				e.preventDefault()
				if (isOpen) {
					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					form.handleSubmit(async () => {
						try {
							await onProjectCreate()
						} catch (error) {
							console.error("Error handling form submission:", error)
							captureError(error, {
								domain: "general",
								action: "create project",
								workspaceId: workspace?.id,
							})
						}
					})()
				}
			},
			{
				// The dialog mounts inside a Radix overlay, which the shared guard treats
				// as a reason to swallow every key.
				skipDefaultGuards: true,
				enableOnFormTags: true,
			},
		)

		useEffect(() => {
			form.setValue("name", title ?? "")
		}, [title, form])

		return (
			<Dialog
				isOpen={isOpen}
				onOpenChange={(newOpen) => onOpenChange(newOpen, undefined)}
				className="m-2 p-0"
				position="right"
				title={
					<div className="px-6 pt-5">
						{teamOptions.length > 0 ? (
							<PopoverButton
								variant="outline"
								className={CHIP_CLASS}
								onChange={(value: string | null) =>
									form.setValue("teamId", value ?? "")
								}
								data={teamOptions}
								selected={teamId}
								placeholder="Select team..."
							/>
						) : (
							<span className="sr-only">
								{tagId ? "Convert to Linear project" : "Create Linear project"}
							</span>
						)}
					</div>
				}
				withOverlay={false}
				onCloseAutoFocus={(event) => event.preventDefault()}
				onInteractOutside={(event) => event.preventDefault()}
				modal={false}
			>
				<ScrollArea>
					<ScrollViewport ref={ref}>
						<Form
							id="create-project-form"
							form={form}
							className="flex h-full flex-col"
							onSubmit={async () => {
								try {
									await onProjectCreate()
								} catch (error) {
									toast.error({
										title:
											error instanceof Error
												? error.message
												: "Couldn't create the project",
									})
								}
							}}
						>
							<div className="flex flex-col gap-3 px-6 pt-4">
								{teamOptions.length === 0 && (
									<Alert>
										<span className="text-secondary">
											It seems you don&apos;t have any Linear teams visible.
											Please select them in the Linear integration settings page
											or make them public in Linear.
										</span>
									</Alert>
								)}
								<div className="flex">
									<ColorPicker
										value={color}
										onChange={(color) => {
											form.setValue("color", color)
										}}
										withIcon
										withEmoji
										icon={icon}
										onIconChange={(icon) => {
											form.setValue("icon", icon)
										}}
									/>
								</div>
								<FormTextarea
									textSize="xl"
									variant="borderless"
									name="name"
									placeholder="Project name"
									maxLength={81}
									className="w-full font-semibold"
								/>
								<FormTextarea
									textSize="md"
									className="font-normal text-gray-600 dark:text-gray-400"
									variant="borderless"
									name="description"
									placeholder="Add a short summary..."
									maxLength={256}
									minRows={1}
									maxRows={6}
								/>
								<div className="flex flex-wrap items-center gap-2">
									<PopoverButton
										variant="outline"
										className={CHIP_CLASS}
										onChange={(value: string | null) =>
											form.setValue("status", value ?? "")
										}
										data={projectStatusOptions}
										selected={status}
										placeholder="Change project status..."
										popoverClassName="min-w-56"
										isLoading={!organization?.projectStatuses}
									/>
									<PopoverButton
										variant="outline"
										className={CHIP_CLASS}
										onChange={(value: string | null) =>
											form.setValue("priority", value ?? undefined)
										}
										data={priorityOptions}
										selected={priority ?? "0"}
										placeholder="Set priority..."
										numberShortcuts
									/>
									<PopoverButton
										variant="outline"
										className={CHIP_CLASS}
										onChange={(value: string | null) =>
											form.setValue("leadId", value ?? undefined)
										}
										data={leadOptions}
										selected={leadId}
										name="Lead"
										icon={LeadIcon}
										placeholder="Set lead..."
										resetOption="No lead"
										resetIcon={LeadIcon}
									/>
									<PopoverButton
										variant="outline"
										onChange={(value: string | null) =>
											form.setValue(
												"memberIds",
												value === null
													? []
													: memberIds.includes(value)
														? memberIds.filter((id) => id !== value)
														: [...memberIds, value],
											)
										}
										data={leadOptions}
										selectedItems={memberIds}
										placeholder="Add members..."
										resetOption="No members"
										resetIcon={MembersIcon}
									>
										<Button
											variant="outline"
											className={cn(
												CHIP_CLASS,
												memberIds.length === 0 &&
													"text-tertiary dark:text-secondary",
											)}
											Icon={MembersIcon}
										>
											{memberIds.length === 0
												? "Members"
												: memberIds.length === 1
													? (members?.find(({ id }) => id === memberIds[0])
															?.name ?? "1 member")
													: `${memberIds.length} members`}
										</Button>
									</PopoverButton>
									<ProjectDateChip
										label="Start"
										Icon={CalendarIcon}
										value={startDate}
										onChange={(value) => form.setValue("startDate", value)}
									/>
									<ProjectDateChip
										label="Target"
										Icon={TargetIcon}
										value={targetDate}
										onChange={(value) => form.setValue("targetDate", value)}
									/>
									<LabelSelect
										labels={projectLabels ?? []}
										selectedLabelIds={labelIds}
										emptyLabel="Labels"
										emptyIcon={LinearLabelsIcon}
										teamId={teamId}
										variant="outline"
										onChange={(labelId: string) =>
											form.setValue(
												"labelIds",
												toggleLabelId(projectLabels ?? [], labelIds, labelId),
											)
										}
									/>
								</div>
								<hr className="border-gray-200 dark:border-gray-700/80" />
							</div>
							<div className="flex-1" />
							<div className="px-6 pb-4">
								<div className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700/80">
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium">Milestones</span>
										<IconButton
											variant="ghost"
											Icon={PlusIcon}
											aria-label="Add milestone"
											onClick={() =>
												setMilestones([
													...milestoneRows,
													{ id: generateId(), name: "" },
												])
											}
										/>
									</div>
									{milestoneRows.map((milestone, index) => (
										<div key={milestone.id} className="flex items-start gap-2">
											<Input
												name={`milestone-${milestone.id}`}
												autoFocus={!milestone.name}
												value={milestone.name}
												placeholder="Milestone name"
												maxLength={80}
												className="flex-1"
												onChange={(event) =>
													setMilestones(
														milestoneRows.map((row, i) =>
															i === index
																? { ...row, name: event.target.value }
																: row,
														),
													)
												}
											/>
											<ProjectDateChip
												label="Target"
												Icon={TargetIcon}
												className="w-28 flex-none justify-start"
												value={milestone.targetDate}
												onChange={(value) =>
													setMilestones(
														milestoneRows.map((row, i) =>
															i === index ? { ...row, targetDate: value } : row,
														),
													)
												}
											/>
											<IconButton
												variant="ghost"
												Icon={RemoveIcon}
												aria-label="Remove milestone"
												onClick={() =>
													setMilestones(
														milestoneRows.filter((_, i) => i !== index),
													)
												}
											/>
										</div>
									))}
								</div>
							</div>
							<div className="border-t border-gray-200 px-6 py-4 dark:border-gray-700/80">
								<InternalDialogFooter>
									<SubmitButton
										disabled={teamOptions.length === 0}
										isLoading={
											createProjectMutation.isLoading ||
											convertToProjectMutation.isLoading
										}
									>
										{tagId ? "Convert to project" : "Create project"}
									</SubmitButton>
								</InternalDialogFooter>
							</div>
						</Form>
					</ScrollViewport>
				</ScrollArea>
			</Dialog>
		)
	},
)
